import { Alert, Button, Divider, Grid, Stack, Typography, Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CardInput from '../../../components/common/Forms/CardInput';
import { ProfileInfo } from '../../../components/common/props-types';
import * as Yup from 'yup';
import { cardValidationRegex } from '../../../components/common/regex';
import { useFormik } from 'formik';
import useI18nForm from '../../../components/common/hooks/useI18nForm';
import config from '../../../config';
import { ReplaceFLSCardResponse } from '../../../services/RegisterFamiliplusCard';
import { GetProfileInfo } from 'services/profile/ProfileService';
import LoadingIndicator from 'components/common/LoadingIndicator';
import useApi, { Status } from 'common/hooks/useApi';
import { useNavigate } from 'common/hooks/useNavigate';
import useAuth0 from 'common/hooks/useAuth0';
import { Helmet } from 'react-helmet';
import { SyncAccount } from 'components/common/syncAccount';

const cardStartingValue = config.CARD_STARTING_VALUE;

const FamiliplusReplaceCard = (): JSX.Element => {
  const navigate = useNavigate();
  const { user, getAccessTokenSilently } = useAuth0();
  const [authProvider, setAuthProvider] = useState({ sessionId: '', token: '' });
  const [fetchProfile, profileInfo, fetchingProfileStatus] = useApi(GetProfileInfo);
  const [apiMessage, setAPIMessage] = useState<{ status: 'success' | 'error' | ''; message: string }>({
    status: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t, i18n } = useTranslation('component');

  const isDataLoading = fetchingProfileStatus === Status.Pending;
  const hasFetchDataFailed = fetchingProfileStatus === Status.Rejected;
  const isDataResolved = fetchingProfileStatus === Status.Resolved;

  useEffect(() => {
    const fetchData = async () => {
      setAuthProvider({ sessionId: user?.session_id, token: await getAccessTokenSilently() });
    };
    fetchData();
  }, [getAccessTokenSilently, user?.session_id]);

  useEffect(() => {
    if (authProvider && authProvider.sessionId.length > 0 && authProvider.token.length > 0) {
      fetchProfile(authProvider);
    }
  }, [authProvider, fetchProfile]);

  useEffect(() => {
    if (profileInfo && (profileInfo.familiplusCard || '').length == 0) {
      navigate(`/${i18n.resolvedLanguage}/${i18n.resolvedLanguage == 'fr' ? 'Compte' : 'Account'}/Familiplus`);
    }
  }, [profileInfo, navigate, i18n.resolvedLanguage]);

  const handleSubmit = async (values: any) => {
    if (!isLoading) {
      setIsLoading(true);
      setAPIMessage({ status: '', message: '' });

      try {
        const familiplusCard = cardStartingValue + values.CardNumber?.replace(/\D/g, '');

        const replaceFLSCardResponse = await ReplaceFLSCardResponse(authProvider, familiplusCard!);
        if (replaceFLSCardResponse.ok) {
          setAPIMessage({
            status: 'success',
            message: t('ReplaceFamiliplusCard.Success', { cardNumber: familiplusCard }).toString(),
          });
          SyncAccount();
        } else {
          const body = await replaceFLSCardResponse.json();
          if (body?.Messages && body.Messages.length == 1) {
            if (body.Messages[0].Code == 13130) {
              setAPIMessage({ status: 'error', message: t('Errors.AlreadyAssociated') });
            } else if (body.Messages[0].Code == 14511) {
              setAPIMessage({ status: 'error', message: t('Errors.Associate404') });
            } else {
              setAPIMessage({ status: 'error', message: t('Errors.GenericError') });
            }
          } else {
            setAPIMessage({ status: 'error', message: t('Errors.GenericError') });
          }
        }
      } catch (e) {
        setAPIMessage({ status: 'error', message: t('Errors.GenericError') });
        return;
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{t('MetaTag.ReplaceCardTitle')}</title>
        <meta name="description" content={t('MetaTag.ReplaceCardDescription').toString()} />
      </Helmet>
      {isDataLoading && <LoadingIndicator />}

      {hasFetchDataFailed && (
        <Alert id="fetchAlert" severity="error" sx={{ mb: '16px' }}>
          {t('Errors.GenericError')}
        </Alert>
      )}

      {isDataResolved && profileInfo?.familiplusCard && (
        <FamiliplusReplaceCardForm
          profileInfo={profileInfo}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
          apiMessage={apiMessage}
        />
      )}
    </>
  );
};

const FamiliplusReplaceCardForm = (props: {
  profileInfo: ProfileInfo;
  handleSubmit: any;
  isLoading: boolean;
  apiMessage: { status: 'success' | 'error' | ''; message: string };
}): JSX.Element => {
  const { t } = useTranslation('component');

  const EditCardSchema = Yup.object().shape({
    CardNumber: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      CardNumber: '',
    },
    validationSchema: EditCardSchema,
    onSubmit: (values: any) => {
      props.handleSubmit(values);
    },
  });

  useI18nForm({ errors: formik.errors, touched: formik.touched, setFieldTouched: formik.setFieldTouched });

  return (
    <form id="familiplusReplaceCardForm" noValidate onSubmit={formik.handleSubmit}>
      <Grid container rowSpacing={2} sx={{ display: 'block' }}>
        <Typography id="replaceFamiliplusCardTitle" variant="title">
          {t('ReplaceFamiliplusCard.Title')}
        </Typography>
        {props.apiMessage.status != '' && (
          <Grid item xs={12}>
            <Stack spacing={2}>
              <Alert id="ReplaceCardAPIMessageAlert" severity={props.apiMessage.status}>
                {props.apiMessage.message}
              </Alert>
            </Stack>
          </Grid>
        )}
        <Grid item xs={12}>
          <Stack spacing={2}>
            <Alert id="ReplaceCardInfoAlert" severity="info">
              {t('ReplaceFamiliplusCard.Info')}
            </Alert>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <CardInput
            formik={formik}
            cardStartingValue={cardStartingValue}
            cardNumberLabel={t('ReplaceFamiliplusCard.CardNumber')}
          />
        </Grid>
      </Grid>
      <Divider />
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row-reverse',
            alignItems: 'center',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {props.isLoading && (
              <Box sx={{ marginRight: '16px' }}>
                <LoadingIndicator />
              </Box>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={
                props.isLoading || !formik.values.CardNumber || !cardValidationRegex.test(formik.values.CardNumber)
              }>
              {t('ReplaceFamiliplusCard.Button')}
            </Button>
          </Box>
        </Box>
      </Grid>
    </form>
  );
};

export default FamiliplusReplaceCard;
